/**
 * The bell, top right: how many mentions you have not looked at, and the list. Fed by
 * `notifications.list`; the socket invalidates it when someone tags you (desk note or Slack
 * reply), so the count moves without a refresh. Opening the list marks what it shows read;
 * a row opens its ticket.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import { dayTime } from '../../lib/format';
import { notificationsQuery } from '../../lib/queries';
import { rpc } from '../../lib/rpc';

export function Notifications() {
  const qc = useQueryClient();
  const q = useQuery(notificationsQuery());
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);
  const read = useMutation({
    mutationFn: (ids?: number[]) => rpc.notifications.markRead(ids ? { ids } : {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications.list'] }),
  });

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = q.data?.unread ?? 0;
  const rows = q.data?.rows ?? [];
  const label = unread === 0 ? 'Notifications' : `${String(unread)} unread ${unread === 1 ? 'notification' : 'notifications'}`;

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        title={label}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className={`relative grid size-8 place-items-center rounded-control ${unread > 0 ? 'text-ink' : 'text-ink-3'} hover:bg-surface-2 hover:text-ink`}
      >
        <Bell className="size-4" />
        {unread > 0 && (
          <span aria-hidden className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-info px-1 text-[10px] font-medium leading-none text-canvas">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div id={panelId} role="dialog" aria-label="Notifications" className="absolute right-0 top-full z-30 mt-1 w-[380px] rounded-panel border border-line bg-surface shadow-[var(--overlay-shadow)]">
          <div className="flex items-baseline gap-3 border-b border-line px-3 py-2">
            <span className="text-emphasis font-medium text-ink">Notifications</span>
            <span className="text-hint text-ink-3">{unread > 0 ? `${String(unread)} unread` : 'Nothing unread'}</span>
            {unread > 0 && (
              <button type="button" className="ml-auto text-hint text-info hover:underline" disabled={read.isPending} onClick={() => { read.mutate(undefined); }}>
                Mark all read
              </button>
            )}
          </div>
          {rows.length === 0 ? (
            <p className="px-3 py-4 text-body text-ink-3">When someone tags you on a ticket, from the desk or from Slack, it shows here.</p>
          ) : (
            <ul className="max-h-[420px] overflow-y-auto">
              {rows.map((n) => (
                <li key={n.id} className={`border-t border-line first:border-t-0 ${n.readAt ? '' : 'border-l-2 border-l-info'}`}>
                  <Link
                    to="/tickets/$ticketId"
                    params={{ ticketId: n.ticketId }}
                    className="block px-3 py-2 hover:bg-surface-2"
                    onClick={() => {
                      if (!n.readAt) read.mutate([n.id]);
                      setOpen(false);
                    }}
                  >
                    <span className="flex items-baseline gap-2">
                      <span className="text-body text-ink">{n.kind === 'scheduled' ? 'Back from Scheduled' : `${n.actorName ?? 'Someone'} mentioned you`}</span>
                      <span className="mono text-hint text-ink-3">{n.ticketNumber}</span>
                      <span className="ml-auto shrink-0 text-hint text-ink-3">{dayTime(n.createdAt)}</span>
                    </span>
                    <span className="block truncate text-hint text-ink-2">{[n.storeLabel, n.subject].filter(Boolean).join(', ')}</span>
                    {n.preview && <span className="block truncate text-hint text-ink-3">{n.preview}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
